import * as React from 'react';

import {
  CurrencyCode,
  Money as MoneyType,
} from '@rematter/frontend-lib-api-client';
import {
  Button,
  Col,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogMain,
  DialogOverlay,
  DialogTitle,
  formatMoney,
  Loading,
  Money,
  MoneyField,
  Row,
  Select,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';

import {
  OpenBillForPayment,
  openBillsForPaymentAtom,
} from '../../_graphql/queryOpenBillsForPayment';
import { $applyPaymentToBill } from '../../_state/paymentDetail.state';

import { useTranslations } from '@/finance/i18n/useTranslations';

type PaymentApplyToBillDialogProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  paymentId: string;
  unappliedAmount?: MoneyType;
  accountId?: string;
  currencyCode?: CurrencyCode;
  onSuccess?: () => void;
};

//
// ⚛️ ⚛️ ⚛️ PAYMENT APPLY TO BILL DIALOG ⚛️ ⚛️ ⚛️
//
export const PaymentApplyToBillDialog: React.FC<
  PaymentApplyToBillDialogProps
> = ({
  isOpen,
  onOpenChange,
  paymentId,
  unappliedAmount,
  accountId,
  currencyCode,
  onSuccess,
}) => {
  // Translations
  const t = useTranslations();

  // States
  const [selectedBillId, setSelectedBillId] = React.useState<
    string | undefined
  >(undefined);
  const [amount, setAmount] = React.useState<number | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  // Atoms
  const billsAtom = React.useMemo(
    () =>
      openBillsForPaymentAtom((_get) => ({
        variables: {
          accountId,
          currencyCode,
        },
        skip: !isOpen,
      })),
    [accountId, currencyCode, isOpen],
  );
  const { data: { bills } = { bills: [] }, isPending: isLoadingBills } =
    useAtomValue(billsAtom);
  const applyToBill = useSetAtom($applyPaymentToBill);

  // Data hooks
  const toast = useToast();

  // Memoized values
  const selectedBill = React.useMemo(
    () => bills.find((b) => b.id === selectedBillId),
    [bills, selectedBillId],
  );

  const maxAmount = React.useMemo(() => {
    if (!selectedBill || !unappliedAmount) return undefined;
    return Math.min(selectedBill.openAmount.amount, unappliedAmount.amount);
  }, [selectedBill, unappliedAmount]);

  const billOptions = React.useMemo(
    () =>
      bills.map((bill: OpenBillForPayment) => ({
        key: bill.id,
        label: `${bill.documentNumber} - ${bill.account?.name ?? t.payments.detail.dialogs.applyToBill.unknownAccount} - ${formatMoney(bill.openAmount.amount, bill.openAmount.currencyCode)} ${t.payments.detail.dialogs.applyToBill.openSuffix}`,
      })),
    [bills, t],
  );

  const canSubmit = selectedBillId && amount != null && amount > 0;

  // Callbacks
  const handleClose = React.useCallback(() => {
    setSelectedBillId(undefined);
    setAmount(undefined);
    onOpenChange(false);
  }, [onOpenChange]);

  const handleBillChange = React.useCallback(
    (key: React.Key) => {
      const billId = key as string;
      setSelectedBillId(billId);
      const bill = bills.find((b) => b.id === billId);
      if (bill) {
        const suggestedAmount = unappliedAmount
          ? Math.min(bill.openAmount.amount, unappliedAmount.amount)
          : bill.openAmount.amount;
        setAmount(suggestedAmount);
      }
    },
    [bills, unappliedAmount],
  );

  const handleSubmit = React.useCallback(async () => {
    if (!selectedBillId || amount == null || !unappliedAmount) return;

    setIsSubmitting(true);
    try {
      await applyToBill({
        paymentId,
        billId: selectedBillId,
        appliedAmount: {
          amount,
          currencyCode: unappliedAmount.currencyCode,
        },
      });
      toast.success(t.payments.detail.messages.paymentApplied);
      onSuccess?.();
      handleClose();
    } catch (err) {
      toast.error(
        t.payments.detail.messages.applyError(
          err instanceof Error ? err.message : t.messages.common.unknownError,
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    selectedBillId,
    amount,
    paymentId,
    unappliedAmount,
    applyToBill,
    toast,
    onSuccess,
    handleClose,
    t,
  ]);

  return (
    <DialogOverlay
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      className="@PaymentApplyToBillDialog"
    >
      <DialogContent position="center">
        <DialogHeader>
          <DialogTitle>
            {t.payments.detail.dialogs.applyToBill.title}
          </DialogTitle>
        </DialogHeader>
        <DialogMain>
          <Col gap4>
            <Loading isLoading={isLoadingBills}>
              {billOptions.length === 0 && (
                <p className="py-4 text-center text-sm text-neutral/60">
                  {t.payments.detail.dialogs.applyToBill.noBillsAvailable}
                </p>
              )}
              {billOptions.length > 0 && (
                <>
                  <Select
                    label={t.payments.detail.dialogs.applyToBill.billLabel}
                    value={selectedBillId}
                    onChange={handleBillChange}
                    options={billOptions}
                    placeholder={
                      t.payments.detail.dialogs.applyToBill.selectBill
                    }
                  />
                  <MoneyField
                    label={t.payments.detail.dialogs.applyToBill.amountLabel}
                    amount={amount ?? 0}
                    onAmountChange={setAmount}
                    currencyCode={unappliedAmount?.currencyCode ?? 'USD'}
                    currencyLocked
                    minValue={0}
                    maxValue={maxAmount}
                  />
                  <Col gap1 className="text-sm text-neutral/60">
                    {unappliedAmount && (
                      <div>
                        {t.payments.detail.dialogs.applyToBill.unappliedBalance}
                        :{' '}
                        <Money
                          amount={unappliedAmount.amount}
                          currencyCode={unappliedAmount.currencyCode}
                        />
                      </div>
                    )}
                    {selectedBill && (
                      <>
                        <div>
                          {t.payments.detail.dialogs.applyToBill.billAmount}:{' '}
                          <Money
                            amount={selectedBill.amount.amount}
                            currencyCode={selectedBill.amount.currencyCode}
                          />
                        </div>
                        <div>
                          {t.payments.detail.dialogs.applyToBill.openBalance}:{' '}
                          <Money
                            amount={selectedBill.openAmount.amount}
                            currencyCode={selectedBill.openAmount.currencyCode}
                          />
                        </div>
                      </>
                    )}
                  </Col>
                </>
              )}
            </Loading>
          </Col>
        </DialogMain>
        <DialogFooter>
          <Row xEnd gap3>
            <Button
              variant="outline"
              onPress={handleClose}
              isDisabled={isSubmitting}
            >
              {t.payments.advances.apply.buttons.cancel}
            </Button>
            <Button
              variant="solid"
              color="primary"
              onPress={handleSubmit}
              isDisabled={!canSubmit}
              isPending={isSubmitting}
            >
              {t.payments.advances.apply.buttons.apply}
            </Button>
          </Row>
        </DialogFooter>
      </DialogContent>
    </DialogOverlay>
  );
};
